import { useEffect, useState, useMemo } from 'react';
import { ETIQUETA_APARATO_SISTEMA, type AparatoSistema } from '@ausentia/shared';
import { abrirPdf, api, ErrorApi } from '../api/cliente.js';
import { useSesion } from '../contexto/Sesion.js';

interface ConstanciaPortal {
  codigo: string;
  fechaControl: string;
  firmadaEn: string;
  resultado: string;
  trabajador: { nombre: string; legajo: string | null };
  aptitud: string | null;
  fechaReinsercion: string | null;
  aparatoSistema: string | null;
  medico: { nombre: string; matricula: string } | null;
  tipoControl: string | null;
  tipoControlNombre: string | null;
  observaciones: string | null;
  contingenciaPapel: boolean;
  actaCodigo: string | null;
  rectificaA: string | null;
  rectificadaPor: string | null;
  documentos: { version: string; hashSha256: string }[];
  adjuntos: { id: string; tipo: string }[];
}

const RESULTADO_TXT: Record<string, string> = {
  REALIZADA: 'Realizada',
  PACIENTE_AUSENTE: 'Ausente',
  RECHAZA_CONTROL: 'Rechazó',
  DOMICILIO_ERRONEO: 'Domicilio erróneo',
  MEDICO_IMPOSIBILITADO: 'Médico no pudo',
};

/** Canal por el que se realizó el control (dato laboral, lo ve también RRHH). */
function viaControl(c: ConstanciaPortal): string {
  const base = c.tipoControlNombre ?? '—';
  return c.contingenciaPapel ? `${base} · papel` : base;
}

const ADJUNTO_TXT: Record<string, string> = {
  CONSTANCIA_PAPEL_FOTO: '📷 Foto del talonario',
  FACHADA_FOTO: '📷 Foto de la fachada',
  EVIDENCIA_FOTO: '📷 Foto de evidencia',
};

export function Constancias() {
  const { portal } = useSesion();
  const esServicioMedico = portal?.rol === 'SERVICIO_MEDICO';
  const [filas, setFilas] = useState<ConstanciaPortal[] | null>(null);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    api
      .get<ConstanciaPortal[]>('/api/portal/constancias')
      .then(setFilas)
      .catch((err) => setError(err instanceof ErrorApi ? err.message : String(err)));
  }, []);

  async function ver(codigo: string, version: 'CLINICA' | 'LABORAL') {
    try {
      await abrirPdf(`/api/portal/constancias/${codigo}/pdf?version=${version}`);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : String(err));
    }
  }

  // Foto del talonario: sólo Servicio Médico (el backend gatea por rol y registra el acceso).
  // abrirPdf reutiliza fetch+blob+window.open, es mime-agnóstico (sirve para imágenes).
  async function verFoto(id: string) {
    try {
      await abrirPdf(`/api/portal/adjuntos/${id}`);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : String(err));
    }
  }

  const totalControles = filas ? filas.length : 0;
  const realizados = useMemo(
    () => (filas ? filas.filter((f) => f.resultado === 'REALIZADA').length : 0),
    [filas],
  );
  const noRealizados = useMemo(
    () => (filas ? filas.filter((f) => f.resultado !== 'REALIZADA').length : 0),
    [filas],
  );

  const filasFiltradas = useMemo(() => {
    if (!filas) return [];
    const busq = busqueda.toLowerCase();
    return filas.filter(
      (f) =>
        f.trabajador.nombre.toLowerCase().includes(busq) ||
        f.codigo.toLowerCase().includes(busq) ||
        (f.aptitud ?? '').toLowerCase().includes(busq),
    );
  }, [filas, busqueda]);

  return (
    <>
      <h2>Constancias recibidas</h2>
      <p className="muted">
        {esServicioMedico
          ? 'Rol Servicio Médico: además del dictamen laboral, ves las observaciones reservadas y la foto del talonario (secreto profesional, art. 156 CP).'
          : 'Rol RRHH: ves si incapacita o no para trabajar, si el control se realizó, por qué vía y quién lo realizó. El médico de control no diagnostica; las notas reservadas y la foto no se muestran.'}
      </p>

      {error && <div className="error-box">{error}</div>}
      {filas === null && <div className="cargando">Cargando…</div>}
      {filas && filas.length === 0 && <p className="muted">Todavía no hay constancias firmadas.</p>}

      {filas && filas.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 12, marginBottom: 20 }}>
            <div className="tarjeta-kpi glass" style={{ borderLeft: '4px solid var(--verde)' }}>
              <span className="kpi-label">TOTAL CONTROLES</span>
              <span className="kpi-value">{totalControles}</span>
              <span className="kpi-subtext">Visitas procesadas</span>
            </div>
            <div className="tarjeta-kpi glass" style={{ borderLeft: '4px solid var(--ambar-fuerte)' }}>
              <span className="kpi-label">CONTROLES REALIZADOS</span>
              <span className="kpi-value">{realizados}</span>
              <span className="kpi-subtext">Con dictamen de aptitud</span>
            </div>
            <div className="tarjeta-kpi glass" style={{ borderLeft: '4px solid var(--rojo)' }}>
              <span className="kpi-label">NO REALIZADOS</span>
              <span className="kpi-value">{noRealizados}</span>
              <span className="kpi-subtext">Ausente / rechazo / no pudo</span>
            </div>
          </div>

          <div className="campo" style={{ marginBottom: 20 }}>
            <input
              type="text"
              placeholder="🔍 Buscar por trabajador, código o aptitud…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </>
      )}

      {filas && filas.length > 0 && filasFiltradas.length === 0 && (
        <p className="muted">No se encontraron constancias que coincidan con la búsqueda.</p>
      )}

      {filasFiltradas.length > 0 && (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Trabajador</th>
                <th>Fecha</th>
                <th>Resultado</th>
                <th>Aptitud</th>
                <th>Aparato/Sistema</th>
                <th>Retorno</th>
                <th>Realizó</th>
                <th>Vía</th>
                {esServicioMedico && <th>Observaciones</th>}
                <th>Documentos</th>
              </tr>
            </thead>
            <tbody>
              {filasFiltradas.map((c) => (
                <tr key={c.codigo}>
                  <td>
                    <strong>{c.trabajador.nombre}</strong>
                    {c.trabajador.legajo && <div className="mini">Legajo {c.trabajador.legajo}</div>}
                    <div className="mini">{c.codigo}</div>
                    {c.rectificadaPor && <span className="insignia ambar">Rectificada por {c.rectificadaPor}</span>}
                    {c.rectificaA && <span className="insignia azul">Rectifica a {c.rectificaA}</span>}
                    {c.contingenciaPapel && <span className="insignia gris">Papel · acta {c.actaCodigo ?? '—'}</span>}
                  </td>
                  <td>{c.fechaControl}</td>
                  <td>{RESULTADO_TXT[c.resultado] ?? c.resultado}</td>
                  <td>
                    {c.aptitud ? (
                      <span className={`insignia ${c.aptitud === 'APTO_PARA_TRABAJAR' ? 'verde' : 'ambar'}`}>
                        {c.aptitud === 'APTO_PARA_TRABAJAR' ? 'NO INCAPACITA' : 'INCAPACITA'}
                      </span>
                    ) : (
                      <span className="mini">—</span>
                    )}
                  </td>
                  <td>
                    {c.aparatoSistema
                      ? (ETIQUETA_APARATO_SISTEMA[c.aparatoSistema as AparatoSistema] ?? c.aparatoSistema)
                      : <span className="mini">—</span>}
                  </td>
                  <td>{c.fechaReinsercion ?? <span className="mini">—</span>}</td>
                  <td>
                    {c.medico ? (
                      <>
                        {c.medico.nombre}
                        <div className="mini">{c.medico.matricula}</div>
                      </>
                    ) : (
                      <span className="mini">—</span>
                    )}
                  </td>
                  <td>{viaControl(c)}</td>
                  {esServicioMedico && (
                    <td>{c.observaciones ? <span className="mini">{c.observaciones}</span> : <span className="mini">—</span>}</td>
                  )}
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {c.documentos.map((d) => (
                        <button key={d.version} className="secundario" style={{ width: 'auto' }} onClick={() => ver(c.codigo, d.version as 'CLINICA' | 'LABORAL')}>
                          PDF {d.version === 'CLINICA' ? 'reservado' : 'laboral'}
                        </button>
                      ))}
                      {esServicioMedico &&
                        c.adjuntos.map((a) => (
                          <button key={a.id} className="secundario" style={{ width: 'auto' }} onClick={() => verFoto(a.id)}>
                            {ADJUNTO_TXT[a.tipo] ?? '📷 Foto'}
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="disclaimer">
        El acceso a cada documento queda asentado en la bitácora de auditoría. La versión reservada (observaciones,
        fundamento de discrepancia y foto del talonario) solo es accesible para el rol Servicio Médico designado por la
        empresa (ADR-0009).
      </p>
    </>
  );
}
